import { useCallback, useMemo } from 'react';

import { CampaignStoreItem, useShowCampaignById_new } from '@givebacks/api/store';
import { Native } from '@givebacks/utils';
import { UseQueryResult } from '@tanstack/react-query';
import { usePlatformService } from 'core/Hooks';
import { useNavigate } from 'react-router-dom';

import { useCrowdfundingProvider } from './Crowdfunding.provider';

type CampaignTranslationSelect = Pick<
  UseQueryResult<
    {
      team: CampaignStoreItem['team_options'];
      participant: CampaignStoreItem['participant_options'];
      benefit_options: CampaignStoreItem['benefit_options'];
    },
    unknown
  >,
  'data' | 'isLoading'
> & {
  isEnabled: boolean;
};

const useCampaignTranslations = (queryData: CampaignTranslationSelect) => {
  const { data, isLoading, isEnabled } = queryData;
  const TRANSLATIONS = useMemo(
    () => ({
      team: data?.team?.convention_singular,
      teams: data?.team?.convention_plural,
      participant: data?.participant?.convention_singular,
      participants: data?.participant?.convention_plural,
      benefits: data?.benefit_options?.title,
    }),
    [data]
  );

  const t = useCallback(
    (keyString: keyof typeof TRANSLATIONS) => {
      if (isLoading && isEnabled) return '';

      return TRANSLATIONS?.[keyString] || keyString;
    },
    [TRANSLATIONS, isLoading, isEnabled]
  );
  return { t, isLoading };
};
/**
 *
 * @deprecated Use the hooks from core instead
 */
const useDonorTranslations = () => {
  const { causeId, campaignId } = useCrowdfundingProvider();
  const { data: campaign, isLoading } = useShowCampaignById_new({ campaignId, causeId });
  const selector = useMemo(
    () =>
      campaign
        ? {
            team: campaign?.team_options,
            participant: campaign?.participant_options,
            benefit_options: campaign?.benefit_options,
          }
        : undefined,
    [campaign]
  );
  return useCampaignTranslations({
    data: selector,
    isLoading,
    isEnabled: !!campaignId && !!causeId,
  });
};

/**
 *
 * @deprecated Use the hooks from core instead
 */
const useManagementTranslations = () => {
  const { causeId, campaignId } = useCrowdfundingProvider();
  const { data, isLoading } = useShowCampaignById_new({
    campaignId,
    causeId,
    select: (campaign) => ({
      team: campaign.team_options,
      participant: campaign.participant_options,
      benefit_options: campaign.benefit_options,
    }),
  });
  return useCampaignTranslations({ data, isLoading, isEnabled: !!campaignId && !!causeId });
};

/**
 * Navigate to a campaign, will handle logic for RallyUp and if it's mobile.
 */
const useNavigateToCampaign = () => {
  const platform = usePlatformService();
  const navigate = useNavigate();

  const navigateToCampaign = useCallback(
    (campaign: CampaignStoreItem, to: string) => {
      if (campaign.type === 'StoreItems::RallyUp' && campaign?.integration?.url) {
        if (platform.isNative) {
          return Native.postURL(campaign?.integration?.url);
        }

        return window.open(campaign?.integration?.url, '_blank');
      }

      return navigate(to);
    },
    [navigate, platform.isNative]
  );

  return navigateToCampaign;
};
export { useDonorTranslations, useManagementTranslations, useNavigateToCampaign };
